import React, { useEffect, useState, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import AtmIcon from "../../assets/AtmIcon";
import BillIcon from "../../assets/BillIcon";
import CreditCardIcon from "../../assets/CreditCardIcon";
import DashboardCardImage from "../../assets/DashboardCardImage";
import DesktopIcon from "../../assets/DesktopIcon";
import HandCard from "../../assets/HandCard";
import HandCardArrowUp from "../../assets/HandCardArrowUp";
import HandCardMoney from "../../assets/HandCardMoney";
import HandCardPlus from "../../assets/HandCardPlus";
import HandDocumentMoneyPurple from "../../assets/HandDocumentMoneyPurple";
import HandDocumentMoneyWhite from "../../assets/HandDocumentMoneyWhite";
import HandKeyhole from "../../assets/HandKeyhole";
import HandMoney from "../../assets/HandMoney";
import HandSuitcaseMoney from "../../assets/HandSuitcaseMoney";
import HandWalletMoney from "../../assets/HandWalletMoney";
import LockIcon from "../../assets/LockIcon";
import MoneyBagIcon from "../../assets/MoneyBagIcon";
import OfficeIcon from "../../assets/OfficeIcon";
import PhoneIcon from "../../assets/PhoneIcon";
import PiggyBankIcon from "../../assets/PiggyBankIcon";
import QuestionIcon from "../../assets/QuestionIcon";
import WhatsappIcon from "../../assets/WhatsappIcon";
import Yape from "../../assets/Yape";
import { useSearch } from "../../context/SearchContext";
import { Banner } from "../../services/PipelineService";
import {
  sendResponseLinkClick,
  sendCardClick,
  sendTutorialVisualized,
  sendTutorialStepClick,
  sendTutorialCtaClick,
} from "../../utils/analyticsEvents";
import { BannerComponent } from "../molecules/Banner";
import { ActionButton } from "../../services/AnswerComponentsService";
import { Tutorial } from "../../services/AnswerComponentsService";
import AndoIcon from "../../assets/AndoIcon";
import WardaditosIcon from "../../assets/Wardaditos";
import CuotealoIcon from "../../assets/Cuotealo";
import HandMoneyPurpleBgIcon from "../../assets/HandMoneyPurpleBgIcon";
import HandCoinsIcon from "../../assets/HandCoinsIcon";
import HandsPhoneCheckIcon from "../../assets/HandsPhoneCheckIcon";
import HandPhoneCheckIcon from "../../assets/HandPhoneCheckIcon";
import CulquiIcon from "../../assets/Culqui";
import YapeEmpresasIcon from "../../assets/YapeEmpresas";

interface ResponseContentProps {
  textResponse: string;
  banners: Banner[];
  actions: ActionButton[];
  tutorials: Tutorial[];
  query: string;
  requestId: string;
  contextId: string;
  suggestionsEnabled: boolean;
}

const removeSpecificParams = (url: URL, setRfidValue: string): string => {
  const paramsToRemove = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "pcid",
    "rfid",
  ];

  paramsToRemove.forEach((param) => {
    url.searchParams.delete(param);
  });

  url.searchParams.set("rfid", setRfidValue);

  url.search = url.search.replace(/%3A/g, ":");

  return url.toString();
};

interface TutorialSectionProps {
  tutorial: Tutorial;
  query: string;
  requestId: string;
}

const TutorialSectionStable: React.FC<TutorialSectionProps> = ({
  tutorial,
  query,
  requestId,
}) => {
  const tutorialUniqueId = `${
    tutorial.name || tutorial.steps?.[0]?.section || "default-tutorial"
  }-${requestId}`;
  const tutorialIdRef = React.useRef(tutorialUniqueId);
  const sectionRef = React.useRef(0);
  const stepRef = React.useRef(0);

  const [activeSection, setActiveSection] = useState(sectionRef.current);
  const [tutorialStep, setTutorialStep] = useState(stepRef.current);

  const updateTutorialStep = useCallback((newStep: number) => {
    stepRef.current = newStep;
    setTutorialStep(newStep);
  }, []);

  const updateSection = useCallback((newSection: number) => {
    sectionRef.current = newSection;
    setActiveSection(newSection);
    stepRef.current = 0;
    setTutorialStep(0);
  }, []);

  useEffect(() => {
    tutorialIdRef.current = tutorialUniqueId;
    sectionRef.current = 0;
    stepRef.current = 0;
    setActiveSection(0);
    setTutorialStep(0);
  }, [tutorialUniqueId]);

  useEffect(() => {
    const lastTaggedTutorialRequestIdKey = "lastTaggedTutorialRequestId";
    const storedRequestId = localStorage.getItem(
      lastTaggedTutorialRequestIdKey,
    );

    if (storedRequestId !== requestId) {
      sendTutorialVisualized(query);
      localStorage.setItem(lastTaggedTutorialRequestIdKey, requestId);
    }
  }, [query, requestId]);

  if (!tutorial.steps || tutorial.steps.length === 0) return null;

  const currentSection = tutorial.steps[activeSection];
  const currentTutorial = currentSection.steps[tutorialStep];
  if (!currentTutorial) return null;

  const removeNumberedPrefix = (text: string): string =>
    text.replace(/^\d+\.\s*/, "");

  const handlePreviousStep = () => {
    if (tutorialStep > 0) {
      sendTutorialStepClick(query, "Izquierda");
      updateTutorialStep(tutorialStep - 1);
    }
  };

  const handleNextStep = () => {
    if (tutorialStep < currentSection.steps.length - 1) {
      sendTutorialStepClick(query, "Derecha");
      updateTutorialStep(tutorialStep + 1);
    }
  };

  const processTutorialText = (text: string) => {
    const normalizedText = text
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
    const bancaMovilRegex = /banca\s*movil/gi;
    const matches = normalizedText.match(bancaMovilRegex);

    if (!matches) return text;

    return (
      <>
        {matches.map((_, i) => (
          <React.Fragment key={i}>
            <a
              href="https://www.viabcp.com/canales/banca-movil"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white font-semibold no-underline"
              onClick={() => sendTutorialCtaClick("Ir a Banca Movil")}
            >
              Ir a Banca Movil
            </a>
          </React.Fragment>
        ))}
      </>
    );
  };

  return (
    <div className="flex flex-col mt-5 p-5 rounded-lg">
      <p className="text-BCP-normal-text text-center font-bold mb-1">
        Paso {currentTutorial.position}
      </p>
      <p className="text-BCP-normal-text text-center text-sm mb-4">
        {removeNumberedPrefix(currentTutorial.text)}
      </p>

      <div className="relative flex flex-col items-center justify-center w-full">
        <div className="z-0 w-full max-w-[320px] md:max-w-[400px] mx-auto relative">
          <img
            src={currentTutorial.image}
            alt="Tutorial"
            className="w-full object-contain mx-auto rounded-lg drop-shadow-lg"
          />

          <div className="absolute inset-0 flex items-center justify-between pointer-events-none">
            <button
              onClick={handlePreviousStep}
              className={`pointer-events-auto bg-BCP-orange text-white flex items-center justify-center rounded-full h-10 w-10 transform -translate-x-4 shadow-md ${
                tutorialStep > 0 ? "opacity-100" : "opacity-0 cursor-default"
              }`}
              disabled={tutorialStep === 0}
              aria-label="Previous step"
            >
              <i className="pi pi-chevron-left"></i>
            </button>

            <button
              onClick={handleNextStep}
              className={`pointer-events-auto bg-BCP-orange text-white flex items-center justify-center rounded-full h-10 w-10 transform translate-x-4 shadow-md ${
                tutorialStep < currentSection.steps.length - 1
                  ? "opacity-100"
                  : "opacity-0 cursor-default"
              }`}
              disabled={tutorialStep >= currentSection.steps.length - 1}
              aria-label="Next step"
            >
              <i className="pi pi-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap justify-center gap-2 mt-6">
        {tutorial.steps.map((section, index) => (
          <button
            key={index}
            onClick={() => updateSection(index)}
            className={`px-4 py-1 text-sm whitespace-nowrap rounded-full transition-colors ${
              activeSection === index
                ? "bg-BCP-orange text-white font-flexo-demi"
                : "text-BCP-normal-text font-flexo-demi hover:bg-gray-100"
            }`}
          >
            {tutorial.steps.length > 1
              ? section.section
              : processTutorialText(section.section)}
          </button>
        ))}
      </div>
    </div>
  );
};

const ResponseContent: React.FC<ResponseContentProps> = ({
  textResponse,
  banners,
  actions,
  tutorials,
  query,
  requestId,
  contextId,
  suggestionsEnabled,
}) => {
  const {
    products,
    handleSearch,
    handleProductosPreaprobadosClick,
    isWaitingForContext,
    contextReceived,
    setIsWaitingForContext,
  } = useSearch();

  useEffect(() => {
    if (isWaitingForContext && contextReceived && products.length > 0) {
      setIsWaitingForContext(false);
      handleProductosPreaprobadosClick("internal", "actionButton");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextReceived]);

  const getIcon = (icon?: string) => {
    if (!icon) return null;
    switch (icon) {
      case "atm":
        return <AtmIcon />;
      case "bank-note":
        return <BillIcon color="#0A47F0" />;
      case "bcp-office":
        return <OfficeIcon />;
      case "card":
        return <CreditCardIcon color="#0A47F0" />;
      case "dashboard-card":
        return <DashboardCardImage />;
      case "hand-card":
        return <HandCard />;
      case "hand-card-arrow-up":
        return <HandCardArrowUp />;
      case "hand-card-money":
        return <HandCardMoney />;
      case "hand-card-plus":
        return <HandCardPlus />;
      case "hand-document-money-purple-bg":
        return <HandDocumentMoneyPurple />;
      case "hand-document-money-white-bg":
        return <HandDocumentMoneyWhite />;
      case "hand-keyhole":
        return <HandKeyhole />;
      case "hand-money":
        return <HandMoney />;
      case "hand-suitcase-money":
        return <HandSuitcaseMoney />;
      case "hand-wallet-money":
        return <HandWalletMoney />;
      case "lock":
        return <LockIcon />;
      case "money-bag":
        return <MoneyBagIcon />;
      case "monitor":
        return <DesktopIcon />;
      case "phone-arrow":
        return <PhoneIcon />;
      case "piggy-bank":
        return <PiggyBankIcon color="#0A47F0" />;
      case "pre-approved-products":
        return <DashboardCardImage />;
      case "question-mark":
        return <QuestionIcon />;
      case "whatsapp":
        return <WhatsappIcon />;
      case "yape":
        return <Yape />;
      case "ando":
        return <AndoIcon />;
      case "hand-money-purple-bg":
        return <HandMoneyPurpleBgIcon />;
      case "hand-coins":
        return <HandCoinsIcon />;
      case "cuotealo":
        return <CuotealoIcon />;
      case "hands-phone-check":
        return <HandsPhoneCheckIcon />;
      case "hand-phone-check":
        return <HandPhoneCheckIcon />;
      case "culqui":
        return <CulquiIcon />;
      case "warda":
        return <WardaditosIcon />;
      case "yape-empresas":
        return <YapeEmpresasIcon />;
    }
  };

  const getIconColorBackground = (icon?: string) => {
    if (!icon) return null;
    switch (icon) {
      case "whatsapp":
        return "bg-[#37A500]";
      case "productos-preaprobados":
        return "bg-white";
      default:
        return "bg-[#EBF4FF]";
    }
  };

  const actionButton = (action: ActionButton) => {
    const buttonContent = () => {
      return (
        <>
          <div
            className={`ml-0 mr-4 my-auto ${
              action.icon === "productos-preaprobados"
                ? "w-16 h-16"
                : "w-12 h-12"
            } min-w-[3rem] min-h-[3rem] ${getIconColorBackground(
              action.icon,
            )} rounded-full flex items-center justify-center`}
          >
            {getIcon(action.icon)}
          </div>
          <div className="flex flex-col">
            <span className="text-start my-auto ml-0 mr-auto text-BCP-dark-blue font-flexo-demi">
              {action.title}
            </span>
            {action.description && (
              <span className="text-start mr-auto text-BCP-normal-text text-sm">
                {action.description}
              </span>
            )}
          </div>
        </>
      );
    };

    if (action.url === "context:request") {
      return (
        <button
          className={`w-full md:w-[390px] md:h-[107px] inline-flex p-4 rounded-2xl border-white border-2 bg-white hover:bg-[#EBF4FF] shadow-md shadow-[#527094]/20 !outline-none`}
          onClick={() => {
            sendCardClick(
              action.title,
              action.id,
              action.url,
              requestId,
              contextId,
            );

            handleProductosPreaprobadosClick("internal", "actionButton");
          }}
        >
          {buttonContent()}
        </button>
      );
    } else if (action.url.startsWith("query:")) {
      return (
        <button
          className={`w-full md:w-[390px] md:h-[107px] inline-flex p-4 rounded-2xl border-white border-2 bg-white hover:bg-[#EBF4FF] shadow-md shadow-[#527094]/20 !outline-none`}
          onClick={() => {
            sendCardClick(
              action.title,
              action.id,
              action.url,
              requestId,
              contextId,
            );
            handleSearch(action.url.split(":")[1], "internal", "actionButton");
          }}
        >
          {buttonContent()}
        </button>
      );
    } else {
      const modifiedUrl = new URL(action.url);
      removeSpecificParams(modifiedUrl, "buscador:card-decision:pilotoIA");

      return (
        <a
          className={`w-full md:w-[390px] md:h-[107px] inline-flex p-4 rounded-2xl border-white border-2 bg-white hover:bg-[#EBF4FF] shadow-md shadow-[#527094]/20 !outline-none`}
          href={modifiedUrl.toString()}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() =>
            sendCardClick(
              action.title,
              action.id,
              action.url,
              requestId,
              contextId,
            )
          }
        >
          {buttonContent()}
        </a>
      );
    }
  };

  return (
    <div
      className={`flex w-full justify-left md:mt-9 sm:mt-0 xs:mt-0 2xs:mt-6 ${
        suggestionsEnabled ? "blur-sm" : ""
      }`}
    >
      <div
        className="w-full mt-2 md:mt-0"
        style={{ overflowWrap: "break-word" }}
      >
        <ReactMarkdown
          className="prose mx-auto text-BCP-normal-text leading-[24px]"
          components={{
            a: ({ href, ...props }) => {
              let modifiedHref = href;

              if (href) {
                const url = new URL(href);
                removeSpecificParams(url, "buscador:respuesta:pilotoIA");
                modifiedHref = url.toString();
              }

              return (
                <a
                  {...props}
                  href={modifiedHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-BCP-orange font-semibold no-underline"
                  onClick={() =>
                    sendResponseLinkClick(
                      query,
                      requestId,
                      modifiedHref!,
                      props.children?.toString() || "",
                      contextId,
                    )
                  }
                >
                  {props.children}
                </a>
              );
            },
          }}
        >
          {textResponse}
        </ReactMarkdown>

        {banners && banners.length > 0 && (
          <div className="space-y-4 mt-4">
            {banners.map((banner, index) => (
              <BannerComponent key={index} banner={banner} />
            ))}
          </div>
        )}

        {actions && actions.length > 0 && (
          <div className="flex flex-wrap gap-4 mt-4">
            {actions.map((action) => (
              <div key={action.id}>{actionButton(action)}</div>
            ))}
          </div>
        )}

        {tutorials && tutorials.length > 0 && (
          <TutorialSectionStable
            tutorial={tutorials[0] as any}
            query={query}
            requestId={requestId}
          />
        )}

        <div className={`text-sm mt-10 text-BCP-dark-gray space-x-1 flex`}>
          <span className="flex space-x-2">
            <a
              href="https://aifindr.ai/comofuncionaaifindr"
              target="_blank"
              rel="noopener noreferrer"
              className="pt-0.5"
            >
              <i className="pi pi-info-circle transition-colors" />
            </a>
            <span>Respuestas creadas con IA. Comprueba si hay errores.</span>
          </span>
        </div>
      </div>
    </div>
  );
};

export default ResponseContent;
